import React, { useState, useRef, useEffect } from "react"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
  faChevronDown,
  faSave,
  faTrash,
  faCheck,
  faEdit,
  faPencilAlt,
} from "@fortawesome/free-solid-svg-icons"
import { Tooltip } from "antd"

import { Template } from "types/ReportingDashboard/reporting.types"

/**
 * Template option interface for dropdown
 */
interface TemplateOption extends Pick<Template, "name" | "timestamp"> {
  key: string // Template ID
}
/**
 * Props for TemplateSelector component
 */
export interface TemplateSelectorProps {
  /** Object containing templates keyed by ID */
  templates: Record<string, Template>
  /** ID of currently active template or null */
  activeTemplate: string | null
  /** Callback when a template is selected */
  onSelectTemplate: (templateKey: string | null) => void
  /** Callback when save template is requested */
  onSaveTemplate: () => void
  /** Callback when edit template is requested */
  onEditTemplate: (templateKey: string) => void
  /** Callback when delete template is requested */
  onDeleteTemplate: (templateKey: string) => void
  /** Whether currently in template editing mode */
  isEditingTemplate?: boolean
  /** ID of template currently being edited or null */
  editingTemplate?: string | null
  /** ID of template applied to active slide (for display purposes) */
  activeSlideTemplateId?: string | null
}

/**
 * Template Selector Component
 *
 * A comprehensive template management component that provides:
 * - Template selection dropdown with visual feedback
 * - Template editing mode with restrictions
 * - Template creation, editing, and deletion operations
 * - Visual indicators for active and editing states
 * - Sorted template list by most recent timestamp
 * - Click-outside handling for dropdown closure
 *
 * @param props - Component props
 * @returns JSX element for template selector interface
 */
const TemplateSelector: React.FC<TemplateSelectorProps> = ({
  templates, // Object: { [id]: { name, ... } }
  activeTemplate, // ID (key for templates object) or null
  onSelectTemplate,
  onSaveTemplate,
  onEditTemplate,
  onDeleteTemplate,
  isEditingTemplate = false,
  editingTemplate = null, // ID (key for templates object)
}) => {
  const [dropdownOpen, setDropdownOpen] = useState<boolean>(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent): void => {
      if (
        dropdownRef.current &&
        event.target instanceof Node &&
        !dropdownRef.current.contains(event.target)
      ) {
        setDropdownOpen(false)
      }
    }

    document.addEventListener("mousedown", handleClickOutside)
    return () => {
      document.removeEventListener("mousedown", handleClickOutside)
    }
  }, [])

  // Get template options as an array
  const templateOptions: TemplateOption[] = Object.keys(templates).map(
    (key: string) => ({
      key,
      name: templates[key]?.name || "Unnamed Template", // Safeguard name access
      timestamp: templates[key]?.timestamp,
    })
  )

  // Sort templates by most recently created/updated
  templateOptions.sort(
    (a: TemplateOption, b: TemplateOption) =>
      (b.timestamp || 0) - (a.timestamp || 0)
  )

  const handleTemplateSelect = (templateKey: string | null): void => {
    if (isEditingTemplate) return
    onSelectTemplate(templateKey)
    setDropdownOpen(false)
  }

  const handleEditTemplate = (
    e: React.MouseEvent<HTMLButtonElement>,
    templateKey: string
  ): void => {
    e.stopPropagation()
    onEditTemplate(templateKey)
    setDropdownOpen(false)
  }

  const handleDeleteTemplate = (
    e: React.MouseEvent<HTMLButtonElement>,
    templateKey: string
  ): void => {
    e.stopPropagation()
    if (isEditingTemplate && editingTemplate === templateKey) return
    onDeleteTemplate(templateKey)
    setDropdownOpen(false) // Close dropdown after delete action
  }

  const handleDropdownToggle = (): void => {
    if (!isEditingTemplate) {
      setDropdownOpen(!dropdownOpen)
    }
  }

  const handleSaveTemplateChanges = (): void => {
    window.dispatchEvent(new CustomEvent("saveTemplateChanges"))
  }

  const currentActiveTemplateName: string = activeTemplate
    ? templates[activeTemplate]?.name || "Unknown Template"
    : "No template selected"

  const currentEditingTemplateName: string = editingTemplate
    ? templates[editingTemplate]?.name || "Unknown Template"
    : ""

  return (
    <div className="px-4 py-3 border-b border-gray-200">
      <div className="flex justify-between items-center mb-2">
        <h3 className="text-md font-semibold m-0">Template</h3>

        {isEditingTemplate ? (
          <div className="flex items-center">
            <span className="text-xs font-medium text-blue-600 mr-2">
              Editing Template
            </span>
            <button
              onClick={handleSaveTemplateChanges}
              className="p-2 px-3 text-white bg-blue-500 hover:bg-blue-600 rounded-md transition-colors"
              title="Save template changes"
              type="button"
            >
              <FontAwesomeIcon icon={faSave} size="sm" />
            </button>
          </div>
        ) : (
          <Tooltip title="Save current slide as new template" placement="left">
            <button
              onClick={onSaveTemplate}
              className="p-2 px-3 text-gray-500 hover:text-green-500 bg-gray-100 hover:bg-green-50 rounded-md transition-colors"
              type="button"
            >
              <FontAwesomeIcon icon={faSave} size="sm" />
            </button>
          </Tooltip>
        )}
      </div>

      <div ref={dropdownRef} className="relative">
        <button
          onClick={handleDropdownToggle}
          className={`w-full px-3 py-2 text-left flex justify-between items-center rounded-md ${
            isEditingTemplate
              ? "bg-blue-50 border border-blue-300 text-blue-700"
              : activeTemplate && templates[activeTemplate] // Check if activeTemplate exists in templates
              ? "bg-green-50 border border-green-200 text-green-700"
              : "bg-gray-100 border border-gray-200 text-gray-700"
          }`}
          disabled={isEditingTemplate}
          type="button"
        >
          <span className="truncate">
            {isEditingTemplate
              ? `Editing: ${currentEditingTemplateName || "..."}`
              : currentActiveTemplateName}
          </span>
          {!isEditingTemplate && (
            <FontAwesomeIcon icon={faChevronDown} className="ml-2" size="sm" />
          )}
          {isEditingTemplate && (
            <FontAwesomeIcon icon={faPencilAlt} className="ml-2" size="sm" />
          )}
        </button>

        {dropdownOpen && !isEditingTemplate && (
          <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-60 overflow-y-auto">
            {templateOptions.length === 0 ? (
              <div className="p-3 text-center text-gray-500 text-sm">
                No templates saved yet
              </div>
            ) : (
              <>
                <div
                  className="px-3 py-2 hover:bg-gray-100 cursor-pointer flex items-center"
                  onClick={() => handleTemplateSelect(null)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e: React.KeyboardEvent<HTMLDivElement>) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault()
                      handleTemplateSelect(null)
                    }
                  }}
                >
                  <span className="flex-grow">None</span>
                  {!activeTemplate && (
                    <FontAwesomeIcon
                      icon={faCheck}
                      className="text-green-600"
                      size="sm"
                    />
                  )}
                </div>

                {templateOptions.map((template: TemplateOption) => (
                  <div
                    key={template.key}
                    className="px-3 py-2 hover:bg-gray-100 cursor-pointer flex items-center justify-between"
                    onClick={() => handleTemplateSelect(template.key)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e: React.KeyboardEvent<HTMLDivElement>) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault()
                        handleTemplateSelect(template.key)
                      }
                    }}
                  >
                    <span className="flex-grow truncate mr-2">
                      {template.name}
                    </span>
                    <div className="flex items-center flex-shrink-0">
                      {activeTemplate === template.key && (
                        <FontAwesomeIcon
                          icon={faCheck}
                          className="text-green-600 mr-2"
                          size="sm"
                        />
                      )}
                      <Tooltip
                        title="Edit this template definition"
                        placement="top"
                      >
                        <button
                          onClick={(e: React.MouseEvent<HTMLButtonElement>) =>
                            handleEditTemplate(e, template.key)
                          }
                          className="text-blue-500 hover:text-blue-700 p-1"
                          type="button"
                        >
                          <FontAwesomeIcon icon={faEdit} size="xs" />
                        </button>
                      </Tooltip>
                      <Tooltip title="Delete this template" placement="top">
                        <button
                          onClick={(e: React.MouseEvent<HTMLButtonElement>) =>
                            handleDeleteTemplate(e, template.key)
                          }
                          className="text-red-500 hover:text-red-700 ml-1 p-1"
                          type="button"
                        >
                          <FontAwesomeIcon icon={faTrash} size="xs" />
                        </button>
                      </Tooltip>
                    </div>
                  </div>
                ))}
              </>
            )}
          </div>
        )}
      </div>

      {isEditingTemplate ? (
        <p className="text-xs text-blue-600 mt-1 mb-0">
          Changes will affect all slides using this template
        </p>
      ) : activeTemplate && templates[activeTemplate] ? (
        <p className="text-xs text-green-600 mt-1 mb-0">
          New slides will use &quot;{templates[activeTemplate]?.name}&quot;
          template
        </p>
      ) : null}
    </div>
  )
}

export default TemplateSelector
